import Button from '@ixigo/iui/Button';
import { toast } from '@ixigo/iui/Toast';
import {
	ResponsiveModal,
	type ResponsiveModalProps,
	SpinningLoaderContent,
} from '@repo/shared/components';
import { useParams, useRouter } from '@tanstack/react-router';
import { Fragment, useEffect, useRef, useState } from 'react';
import type { BookingSuccessResponse } from '@/api';
import { NavigationHeader } from '@/components';
import { TripDetailsCard } from '@/components/trip-details-card';
import { initiateReschedule } from '../../-api/reschedule';
import { ModifyDetail } from './modify-detail';
import { RescheduleFailure, RescheduleSuccess } from './result-screens';

interface RescheduleProps extends Omit<ResponsiveModalProps, 'children'> {
	open: boolean;
	onModalClosed: () => void;
	onRouterOperationStart: () => void;
	tripDetail: Pick<
		BookingSuccessResponse['data'],
		'rideDetails' | 'cabDetail' | 'rescheduleDetail'
	>;
}

type ResultScreen = 'loading' | 'success' | 'failure';

export function Reschedule({
	onModalClosed,
	onRouterOperationStart,
	tripDetail,
	open,
	...rest
}: RescheduleProps) {
	const { bookingId } = useParams({ from: '/_auth/trips/$bookingId/' });
	const { rideDetails, cabDetail, rescheduleDetail } = tripDetail;
	const [showFullscreen, setShowFullscreen] = useState(true);
	const [internalOpen, setInternalOpen] = useState(false);
	const router = useRouter();
	const [resultScreen, setResultScreen] = useState<ResultScreen | null>(null);
	const intialPickupDateTime = rideDetails.pickup.dateTime.time;
	const abortController = useRef<AbortController | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [dateTime, setDateTime] = useState(intialPickupDateTime);
	const exitCallbackRef = useRef<() => void>(null);

	// Sync internal state with external open prop
	useEffect(() => {
		setInternalOpen(open);
	}, [open]);

	const handleReschedule = async () => {
		if (dateTime === intialPickupDateTime) {
			toast.error('Please select a different date and time', {
				duration: 2000,
				containerStyle: {
					bottom: '90px',
				},
			});
			return;
		}
		const startTime = performance.now();
		setResultScreen('loading');
		abortController.current = new AbortController();
		const initiateRescheduleResponse = await initiateReschedule(
			bookingId,
			dateTime,
			{
				signal: abortController.current.signal,
			},
		);
		if ('data' in initiateRescheduleResponse) {
			onRouterOperationStart?.();
			await router.invalidate();
			setResultScreen('success');
			setShowFullscreen(false);
			exitCallbackRef.current = () => {
				window.scrollTo({
					top: 0,
					behavior: 'smooth',
				});
			};
			return;
		}
		const endTime = performance.now();
		const minTimeToShowLoader = 600;
		if (endTime - startTime > minTimeToShowLoader) {
			setResultScreen('failure');
		} else {
			timeoutRef.current = setTimeout(
				() => {
					setResultScreen('failure');
				},
				minTimeToShowLoader - (endTime - startTime),
			);
		}
		setShowFullscreen(false);
		exitCallbackRef.current = () => {
			setResultScreen(null);
			setShowFullscreen(true);
		};
	};

	const renderResultScreen = (resultScreen: ResultScreen) => {
		const contentMap: Record<string, React.ReactNode> = {
			loading: (
				<SpinningLoaderContent
					descriptions={[
						'Initiating your reschedule request...',
						'Please wait while we process your request',
					]}
					className="fixed top-0 left-0 h-screen w-screen flex flex-col gap-2.5 justify-center items-center bg-primary"
				/>
			),
			success: <RescheduleSuccess onClose={() => setInternalOpen(false)} />,
			failure: (
				<RescheduleFailure
					onClose={() => setInternalOpen(false)}
					onGetHelp={() => {
						// Todo: add redirection to support page
					}}
					onTryAgain={handleReschedule}
				/>
			),
		};
		return contentMap[resultScreen];
	};

	return (
		<ResponsiveModal
			open={internalOpen}
			onClose={() => setInternalOpen(false)}
			fullScreen={showFullscreen}
			transitionDirection={showFullscreen ? 'right' : 'up'}
			classes={{
				container: 'overflow-y-auto flex flex-col',
			}}
			{...rest}
			TransitionProps={{
				onExited: () => {
					if (exitCallbackRef.current) {
						exitCallbackRef.current();
						exitCallbackRef.current = null;
					}
					onModalClosed();
				},
			}}
		>
			{resultScreen ? (
				renderResultScreen(resultScreen)
			) : (
				<Fragment>
					<NavigationHeader
						title="Reschedule Booking"
						leftAction={() => onModalClosed()}
						className="border-b border-tertiary"
					/>
					{/* main content */}
					<div className="bg-tertiary flex-1 flex flex-col gap-y-10">
						<div className="bg-primary p-20 flex flex-col gap-y-15">
							<TripDetailsCard rideDetails={rideDetails} cabInfo={cabDetail} />
						</div>
						<ModifyDetail
							rideDetails={rideDetails}
							note={rescheduleDetail?.note}
							onChange={(dateTime) => {
								setDateTime(dateTime);
							}}
						/>
					</div>
					<div className="sticky bottom-0 w-full bg-primary border-t border-tertiary px-20 py-2.5">
						<Button onClick={handleReschedule} className="w-full" size="lg">
							Reschedule
						</Button>
					</div>
				</Fragment>
			)}
		</ResponsiveModal>
	);
}
